import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PakistanCity } from '../types/domain';
import { InlineSpinner } from './LoadingIndicators';

interface CityMultiSelectProps {
  disabled?: boolean;
  error: boolean;
  loading: boolean;
  onChange: (cities: string[]) => void;
  options: PakistanCity[];
  value: string[];
}

interface PopoverPosition {
  left: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
  width: number;
}

export function CityMultiSelect({
  disabled = false,
  error,
  loading,
  onChange,
  options,
  value,
}: CityMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selectedNames = useMemo(
    () => new Set(value.map((city) => city.toLocaleLowerCase())),
    [value],
  );
  const filteredOptions = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return options;
    return options.filter((city) => (
      `${city.name} ${city.province}`.toLocaleLowerCase().includes(query)
    ));
  }, [options, search]);

  const positionPopover = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const viewportMargin = 12;
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - viewportMargin;
    const spaceAbove = rect.top - viewportMargin;
    const opensAbove = spaceBelow < 260 && spaceAbove > spaceBelow;
    const availableHeight = Math.max(180, (opensAbove ? spaceAbove : spaceBelow) - gap);
    setPosition({
      ...(opensAbove
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
      left: rect.left,
      maxHeight: Math.min(360, availableHeight),
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!anchorRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const frame = window.requestAnimationFrame(() => {
      positionPopover();
      searchRef.current?.focus();
    });
    document.addEventListener('mousedown', closeOnOutsideClick);
    window.addEventListener('resize', positionPopover);
    window.addEventListener('scroll', positionPopover, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('mousedown', closeOnOutsideClick);
      window.removeEventListener('resize', positionPopover);
      window.removeEventListener('scroll', positionPopover, true);
    };
  }, [open, positionPopover]);

  function toggle(city: string) {
    const isSelected = value.some((selected) => selected.toLocaleLowerCase() === city.toLocaleLowerCase());
    onChange(isSelected ? value.filter((selected) => selected !== city) : [...value, city]);
  }

  const summary = value.length === 0
    ? 'Any city'
    : value.length <= 2
      ? value.join(', ')
      : `${value.length} cities selected`;

  return (
    <div className="city-multi-select" ref={anchorRef}>
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Allowed cities"
        className="city-multi-select-trigger"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        type="button"
      >
        <span className={value.length === 0 ? 'placeholder' : ''}>{summary}</span>
        <ChevronDown aria-hidden="true" size={17} />
      </button>
      {open && position ? createPortal(
        <div
          className="city-multi-select-popover"
          ref={popoverRef}
          style={{
            ...(position.top === undefined ? {} : { top: position.top }),
            ...(position.bottom === undefined ? {} : { bottom: position.bottom }),
            left: position.left,
            maxHeight: position.maxHeight,
            width: position.width,
          }}
        >
          <div className="city-multi-select-heading">
            <label className="city-multi-select-search">
              <Search aria-hidden="true" size={16} />
              <input
                aria-label="Search Pakistan cities"
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search city or province"
                ref={searchRef}
                type="search"
                value={search}
              />
            </label>
            {value.length ? <button aria-label="Clear selected cities" className="icon-button" onClick={() => onChange([])} type="button"><X size={16} /></button> : null}
          </div>
          <div aria-label="Pakistan cities" className="city-multi-select-options" role="listbox" aria-multiselectable="true">
            {loading ? <p><InlineSpinner /> Loading cities…</p> : null}
            {error ? <p>Unable to load the Pakistan city catalogue.</p> : null}
            {!loading && !error && filteredOptions.length === 0 ? <p>No cities match this search.</p> : null}
            {filteredOptions.map((city) => {
              const selected = selectedNames.has(city.name.toLocaleLowerCase());
              return (
                <button aria-selected={selected} key={city.id} onClick={() => toggle(city.name)} role="option" type="button">
                  <span className={`city-multi-select-check${selected ? ' selected' : ''}`}>{selected ? <Check size={14} /> : null}</span>
                  <span><strong>{city.name}</strong><small>{city.province}</small></span>
                </button>
              );
            })}
          </div>
          <div className="city-multi-select-footer">
            <span>{value.length ? `${value.length} selected` : 'No city restriction'}</span>
            <button className="text-button" onClick={() => setOpen(false)} type="button">Done</button>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
